// src/utils/helpers.ts

import { AnnotationDataBoundingBox } from '../types/models/annotation'
import { Model } from '../types/models/model'

export const toTitleCase = (str: string): string => {
  // List of small words that should not be capitalized unless they're the first or last word
  const smallWords = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'but',
    'by',
    'for',
    'if',
    'in',
    'nor',
    'of',
    'on',
    'or',
    'per',
    'the',
    'to',
    'via',
  ])

  return str.toLowerCase().replace(/([^\s:-_]+)([\s:-_])*/g, (_, word, separator, index, title) => {
    // Always capitalize the first and last word
    if (index === 0 || index + word.length === title.length) {
      return (
        word.charAt(0).toUpperCase() + word.slice(1) + (separator === '_' ? ' ' : separator || '')
      )
    }

    // Capitalize all words that are not in the smallWords set
    if (!smallWords.has(word)) {
      return (
        word.charAt(0).toUpperCase() + word.slice(1) + (separator === '_' ? ' ' : separator || '')
      )
    }

    // Keep small words lowercase
    return word + (separator || '')
  })
}

/**
 * Calculates a rating for a model based on its mAP, Cross Entropy, and Smooth L1 scores.
 * @param mAP - Mean Average Precision (0-100)
 * @param crossEntropy - Cross Entropy loss (lower is better)
 * @param smoothL1 - Smooth L1 loss (lower is better)
 * @returns A rating between 1 and 5, with 5 being the best
 */
export const rateModel = (mAP: number, crossEntropy: number, smoothL1: number): number => {
  // Ensure mAP is between 0 and 100
  mAP = Math.max(0, Math.min(100, mAP))

  // Calculate individual scores
  const mAPScore = mAP / 20 // 100% mAP would contribute 5 points
  const crossEntropyScore = Math.max(0, 5 - crossEntropy / 10) // Lower is better, max 5 points
  const smoothL1Score = Math.max(0, 5 - smoothL1 / 5) // Lower is better, max 5 points

  // Calculate weighted average (adjust weights as needed)
  const weightedScore = mAPScore * 0.5 + crossEntropyScore * 0.25 + smoothL1Score * 0.25

  // Ensure final score is between 1 and 5
  return Math.max(1, Math.min(5, weightedScore))
}

export const rateClassificationModel = (
  model: Model
): { classificationRating: number; fitStatus: string } => {
  if (model.metrics) {
    const validationAccuracy = model.metrics['validation:accuracy'] as number
    const trainAccuracy = model.metrics['train:accuracy'] as number

    // Calculate individual scores
    const validationScore = validationAccuracy * 5 // Scale to 5 points
    // Scale to 5 points
    // Calculate overall scores
    const trainOverallScore = trainAccuracy * 5
    const valOverallScore = validationScore

    // Determine fit status
    let fitStatus = 'Good fit'
    const accuracyDifference = trainAccuracy - validationAccuracy
    if (accuracyDifference > 0.05) {
      fitStatus = 'Potential overfitting'
    } else if (validationAccuracy < 0.6 && trainAccuracy < 0.6) {
      fitStatus = 'Potential underfitting'
    }

    // Calculate final rating
    let finalRating = trainOverallScore * 0.4 + valOverallScore * 0.6

    // Penalize for overfitting or underfitting
    if (fitStatus !== 'Good fit') {
      finalRating *= 0.8
    }

    // Ensure final rating is between 1 and 5
    finalRating = Math.max(1, Math.min(5, finalRating))

    return { classificationRating: finalRating, fitStatus }
  } else {
    return { classificationRating: 0, fitStatus: 'unk' }
  }
}

/**
 * Calculates a rating for a model based on its performance metrics and fit characteristics.
 * @param model - The model
 * @returns A rating between 1 and 5, with 5 being the best
 */
export const rateBBoxModel = (model: Model): { bBoxRating: number; fitStatus: string } => {
  const { metrics } = model
  if (metrics) {
    // Extract updated validation metrics
    const valMetrics = {
      mAP50: getSafeMetricValue('validation:mAP50', metrics),
      precision: getSafeMetricValue('validation:precision', metrics),
      recall: getSafeMetricValue('validation:recall', metrics),
      boxLoss: getSafeMetricValue('validation:val/box_loss', metrics),
      clsLoss: getSafeMetricValue('validation:val/cls_loss', metrics),
      dflLoss: getSafeMetricValue('validation:val/dfl_loss', metrics),
    }

    // Helper function to calculate individual metric scores
    const calculateScore = (metric: number, isLoss = false) => {
      if (isLoss) {
        return Math.max(0, 5 - metric / 2) // Adjusted for new loss scale
      }
      return Math.min(5, metric * 5) // Scaled for high mAP, precision, recall
    }

    // Calculate scores based on the new metrics
    const valScores = {
      mAP50: calculateScore(valMetrics.mAP50),
      precision: calculateScore(valMetrics.precision),
      recall: calculateScore(valMetrics.recall),
      boxLoss: calculateScore(valMetrics.boxLoss, true),
      clsLoss: calculateScore(valMetrics.clsLoss, true),
      dflLoss: calculateScore(valMetrics.dflLoss, true),
    }

    // Calculate overall validation score with weighted components
    const valOverallScore =
      valScores.mAP50 * 0.4 +
      valScores.precision * 0.2 +
      valScores.recall * 0.2 +
      valScores.boxLoss * 0.1 +
      valScores.clsLoss * 0.05 +
      valScores.dflLoss * 0.05

    // Determine fit status based on overall score
    let fitStatus = 'Good fit'
    if (valOverallScore < 2) {
      fitStatus = 'Potential underfitting'
    } else if (
      valOverallScore > 4.5 &&
      (valScores.boxLoss < 2 || valScores.clsLoss < 2 || valScores.dflLoss < 2)
    ) {
      fitStatus = 'Potential overfitting'
    }

    // Final rating, adjusting for overfitting or underfitting
    let finalRating = valOverallScore
    if (fitStatus !== 'Good fit') {
      finalRating *= 0.8
    }

    // Ensure final rating is between 1 and 5
    finalRating = Math.max(1, Math.min(5, finalRating))

    return { bBoxRating: finalRating, fitStatus }
  } else {
    return { bBoxRating: 0, fitStatus: 'unk' }
  }
}

export const getSafeMetricValue = (key: string, metrics: Record<string, unknown>): number => {
  const value = metrics[key]
  return typeof value === 'number' ? value : 0
}

// Add this helper function at the top of the file, before the component
export const parseColorToRGBA = (color: number, alpha = 1) => {
  const r = parseInt(color.toString(16).substring(0, 2), 16)
  const g = parseInt(color.toString(16).substring(2, 4), 16)
  const b = parseInt(color.toString(16).substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// Helper function to determine text color based on background color
export const getContrastYIQ = (hexcolor: string) => {
  const r = parseInt(hexcolor.substring(1, 3), 16)
  const g = parseInt(hexcolor.substring(3, 5), 16)
  const b = parseInt(hexcolor.substring(5, 7), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 128 ? 'black' : 'white'
}

class SeededRandom {
  private seed: number

  constructor(seed: number) {
    this.seed = seed
  }

  // Linear Congruential Generator (LCG)
  public random(): number {
    const a = 1664525
    const c = 1013904223
    const m = Math.pow(2, 32)
    this.seed = (a * this.seed + c) % m
    return this.seed / m
  }
}

// Function to get a high-resolution time-based seed
function getHighResSeed(): number {
  return Math.floor(performance.now() * 1000)
}

interface ColorResult {
  number: number
  hexColor: string
}

export const generateRandomColor = (): ColorResult => {
  const seed = getHighResSeed()
  const seededRandomColor = new SeededRandom(seed)
  const red = Math.round(seededRandomColor.random() * 255)
  const green = Math.round(seededRandomColor.random() * 255)
  const blue = Math.round(seededRandomColor.random() * 255)

  // const whiteDistance = Math.sqrt(red ** 2 + green ** 2 + blue ** 2)
  // const blackDistance = Math.sqrt((red - 255) ** 2 + (green - 255) ** 2 + (blue - 255) ** 2)

  // Combine the components into a single uint32 integer
  const numberValue = ((red << 16) | (green << 8) | blue) >>> 0

  // Generate hex value
  const hexValue = `#${red.toString(16).padStart(2, '0')}${green
    .toString(16)
    .padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`

  return {
    number: numberValue,
    hexColor: hexValue,
  }
}

export const formatNumber = (num: number): string => {
  const suffixes: string[] = ['', 'K', 'M', 'B', 'T']
  let formattedNum = num
  let magnitude = 0

  while (Math.abs(formattedNum) >= 1000 && magnitude < suffixes.length - 1) {
    magnitude += 1
    formattedNum /= 1000
  }

  if (magnitude === 0) {
    return formattedNum.toFixed(2).replace(/\.?0+$/, '')
  }

  return `${formattedNum.toFixed(1).replace(/\.0$/, '')}${suffixes[magnitude]}`
}

/**
 * Update the normalizeBoundingBox function to work with points primarily
 */
export const normalizeBoundingBoxWithPoints = (
  box: AnnotationDataBoundingBox,
  index?: number | undefined,
  imageWidth?: number,
  imageHeight?: number
): AnnotationDataBoundingBox => {
  // First migrate to ensure consistency
  const migratedBox = migrateBoundingBoxToPoints(box, imageWidth, imageHeight)

  // Ensure points are valid
  if (!migratedBox.points || migratedBox.points.length !== 8) {
    // Fallback to creating points from min/max if available
    if (
      migratedBox.xmin !== undefined &&
      migratedBox.ymin !== undefined &&
      migratedBox.xmax !== undefined &&
      migratedBox.ymax !== undefined
    ) {
      migratedBox.points = createAxisAlignedPoints(
        migratedBox.xmin,
        migratedBox.ymin,
        migratedBox.xmax,
        migratedBox.ymax,
        imageWidth,
        imageHeight
      )
    } else {
      // Create default points if nothing is available
      migratedBox.points = [0, 0, 0, 0, 0, 0, 0, 0]
    }
  }

  // Recalculate min/max from points for consistency
  let pixelPoints = migratedBox.points
  if (imageWidth && imageHeight) {
    pixelPoints = normalizedPointsToPixels(migratedBox.points, imageWidth, imageHeight)
  }
  const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(pixelPoints)

  return {
    ...migratedBox,
    xmin,
    ymin,
    xmax,
    ymax,
    class_index: index !== undefined ? index : migratedBox.class_index,
    confidence: migratedBox.confidence,
  }
}

export const normalizeBoundingBox = (
  box: AnnotationDataBoundingBox,
  index?: number | undefined
): AnnotationDataBoundingBox => {
  // Use the new points-based normalization function without image dimensions for legacy compatibility
  return normalizeBoundingBoxWithPoints(box, index)
}

export const arraysMatchUnordered = (arr1: string[], arr2: string[]): boolean => {
  if (arr1.length !== arr2.length) {
    return false
  }

  const sortedArr1 = [...arr1].sort()
  const sortedArr2 = [...arr2].sort()

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false
    }
  }

  return true
}

// ============================================================================
// COORDINATE SYSTEM CONVERSION UTILITIES
// ============================================================================

/**
 * Convert normalized points (0-1) to image pixel coordinates
 */
export const normalizedPointsToPixels = (
  normalizedPoints: number[],
  imageWidth: number,
  imageHeight: number
): number[] => {
  if (normalizedPoints.length !== 8) {
    throw new Error('Points array must contain exactly 8 coordinates')
  }

  return [
    normalizedPoints[0] * imageWidth, // x1
    normalizedPoints[1] * imageHeight, // y1
    normalizedPoints[2] * imageWidth, // x2
    normalizedPoints[3] * imageHeight, // y2
    normalizedPoints[4] * imageWidth, // x3
    normalizedPoints[5] * imageHeight, // y3
    normalizedPoints[6] * imageWidth, // x4
    normalizedPoints[7] * imageHeight, // y4
  ]
}

/**
 * Convert content-space pixel coordinates to natural image pixel coordinates.
 *
 * After the API transform, all boxes have points in content-space pixels (using content.width/content.height).
 * This function scales them to natural image pixel space (using naturalWidth/naturalHeight).
 *
 * @param contentSpacePoints - Points in content-space pixels (from API transform)
 * @param contentWidth - Content metadata width (used by API to convert from normalized)
 * @param contentHeight - Content metadata height (used by API to convert from normalized)
 * @param naturalWidth - Natural image width (actual image dimensions)
 * @param naturalHeight - Natural image height (actual image dimensions)
 */
export const contentSpacePixelsToNaturalPixels = (
  contentSpacePoints: number[],
  contentWidth: number,
  contentHeight: number,
  naturalWidth: number,
  naturalHeight: number
): number[] => {
  if (contentSpacePoints.length !== 8) {
    throw new Error('Points array must contain exactly 8 coordinates')
  }

  // If content dimensions match natural dimensions, no scaling needed
  if (contentWidth === naturalWidth && contentHeight === naturalHeight) {
    return [...contentSpacePoints]
  }

  // Scale from content space to natural image space
  const scaleX = naturalWidth / contentWidth
  const scaleY = naturalHeight / contentHeight

  return [
    contentSpacePoints[0] * scaleX, // x1
    contentSpacePoints[1] * scaleY, // y1
    contentSpacePoints[2] * scaleX, // x2
    contentSpacePoints[3] * scaleY, // y2
    contentSpacePoints[4] * scaleX, // x3
    contentSpacePoints[5] * scaleY, // y3
    contentSpacePoints[6] * scaleX, // x4
    contentSpacePoints[7] * scaleY, // y4
  ]
}

/**
 * Convert image pixel coordinates to normalized points (0-1)
 */
export const pixelPointsToNormalized = (
  pixelPoints: number[],
  imageWidth: number,
  imageHeight: number
): number[] => {
  if (pixelPoints.length !== 8) {
    throw new Error('Points array must contain exactly 8 coordinates')
  }

  return [
    pixelPoints[0] / imageWidth, // x1
    pixelPoints[1] / imageHeight, // y1
    pixelPoints[2] / imageWidth, // x2
    pixelPoints[3] / imageHeight, // y2
    pixelPoints[4] / imageWidth, // x3
    pixelPoints[5] / imageHeight, // y3
    pixelPoints[6] / imageWidth, // x4
    pixelPoints[7] / imageHeight, // y4
  ]
}

// ============================================================================
// ORIENTED BOUNDING BOX UTILITIES
// ============================================================================

// Point utilities for oriented bounding boxes
export interface Point {
  x: number
  y: number
}

export interface BoundingBoxPoints {
  topLeft: Point
  topRight: Point
  bottomRight: Point
  bottomLeft: Point
}

/**
 * Convert points array to structured points object
 * Points format: [X1, Y1, X2, Y2, X3, Y3, X4, Y4]
 * Order: top-left, top-right, bottom-right, bottom-left
 */
export const pointsArrayToStructured = (points: number[]): BoundingBoxPoints => {
  if (points.length !== 8) {
    throw new Error('Points array must contain exactly 8 coordinates')
  }
  return {
    topLeft: { x: points[0], y: points[1] },
    topRight: { x: points[2], y: points[3] },
    bottomRight: { x: points[4], y: points[5] },
    bottomLeft: { x: points[6], y: points[7] },
  }
}

/**
 * Convert structured points to array
 */
export const structuredPointsToArray = (points: BoundingBoxPoints): number[] => {
  return [
    points.topLeft.x,
    points.topLeft.y,
    points.topRight.x,
    points.topRight.y,
    points.bottomRight.x,
    points.bottomRight.y,
    points.bottomLeft.x,
    points.bottomLeft.y,
  ]
}

/**
 * Calculate min/max from points - handles both normalized and pixel coordinates
 */
export const getMinMaxFromPoints = (
  points: number[]
): { xmin: number; ymin: number; xmax: number; ymax: number } => {
  if (points.length !== 8) {
    throw new Error('Points array must contain exactly 8 coordinates')
  }

  const xCoords = [points[0], points[2], points[4], points[6]]
  const yCoords = [points[1], points[3], points[5], points[7]]

  return {
    xmin: Math.min(...xCoords),
    ymin: Math.min(...yCoords),
    xmax: Math.max(...xCoords),
    ymax: Math.max(...yCoords),
  }
}

/**
 * Create axis-aligned points from min/max - handles coordinate system conversion
 * If imageWidth/Height provided, creates normalized points from pixel min/max
 * Otherwise creates points in the same coordinate system as min/max
 */
export const createAxisAlignedPoints = (
  xmin: number,
  ymin: number,
  xmax: number,
  ymax: number,
  imageWidth?: number,
  imageHeight?: number
): number[] => {
  const pixelPoints = [
    xmin,
    ymin, // top-left
    xmax,
    ymin, // top-right
    xmax,
    ymax, // bottom-right
    xmin,
    ymax, // bottom-left
  ]

  // If image dimensions provided, convert to normalized coordinates
  if (imageWidth && imageHeight) {
    return pixelPointsToNormalized(pixelPoints, imageWidth, imageHeight)
  }

  return pixelPoints
}

/**
 * Check if bounding box is axis-aligned
 */
export const isAxisAligned = (points: number[]): boolean => {
  if (points.length !== 8) return false

  const [x1, y1, x2, y2, x3, y3, x4, y4] = points

  // Check if it forms a rectangle with sides parallel to axes
  return (
    y1 === y2 && // top edge horizontal
    y3 === y4 && // bottom edge horizontal
    x1 === x4 && // left edge vertical
    x2 === x3 // right edge vertical
  )
}

/**
 * Calculate bounding box area from points using shoelace formula
 */
export const calculateAreaFromPoints = (points: number[]): number => {
  if (points.length !== 8) return 0

  // Use shoelace formula for polygon area
  const vertices = pointsArrayToStructured(points)
  const coords = [vertices.topLeft, vertices.topRight, vertices.bottomRight, vertices.bottomLeft]

  let area = 0
  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length
    area += coords[i].x * coords[j].y
    area -= coords[j].x * coords[i].y
  }
  return Math.abs(area) / 2
}

/**
 * Check if point is inside oriented bounding box - handles coordinate conversion
 */
export const isPointInOBB = (
  testPoint: Point,
  points: number[],
  imageWidth?: number,
  imageHeight?: number
): boolean => {
  if (points.length !== 8) return false

  // Convert normalized points to pixel coordinates if needed
  let pixelPoints = points
  if (imageWidth && imageHeight) {
    pixelPoints = normalizedPointsToPixels(points, imageWidth, imageHeight)
  }

  const bbox = pointsArrayToStructured(pixelPoints)
  const vertices = [bbox.topLeft, bbox.topRight, bbox.bottomRight, bbox.bottomLeft]

  // Use ray casting algorithm
  let inside = false
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    if (
      vertices[i].y > testPoint.y !== vertices[j].y > testPoint.y &&
      testPoint.x <
        ((vertices[j].x - vertices[i].x) * (testPoint.y - vertices[i].y)) /
          (vertices[j].y - vertices[i].y) +
          vertices[i].x
    ) {
      inside = !inside
    }
  }
  return inside
}

/**
 * Get center point of bounding box - handles coordinate conversion
 */
export const getBoundingBoxCenter = (
  points: number[],
  imageWidth?: number,
  imageHeight?: number
): Point => {
  if (points.length !== 8) return { x: 0, y: 0 }

  // Convert normalized points to pixel coordinates if needed
  let pixelPoints = points
  if (imageWidth && imageHeight) {
    pixelPoints = normalizedPointsToPixels(points, imageWidth, imageHeight)
  }

  const bbox = pointsArrayToStructured(pixelPoints)
  return {
    x: (bbox.topLeft.x + bbox.topRight.x + bbox.bottomRight.x + bbox.bottomLeft.x) / 4,
    y: (bbox.topLeft.y + bbox.topRight.y + bbox.bottomRight.y + bbox.bottomLeft.y) / 4,
  }
}

/**
 * Get the distance between two points
 */
export const getPointDistance = (point1: Point, point2: Point): number => {
  return Math.sqrt(Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2))
}

/**
 * Rotate a point around a center point by angle in radians
 */
export const rotatePoint = (point: Point, center: Point, angle: number): Point => {
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)

  const dx = point.x - center.x
  const dy = point.y - center.y

  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  }
}

/**
 * Get the angle of rotation of a bounding box in radians
 */
export const getBoundingBoxRotation = (points: number[]): number => {
  if (points.length !== 8) return 0

  const bbox = pointsArrayToStructured(points)
  // Calculate angle from top-left to top-right
  const dx = bbox.topRight.x - bbox.topLeft.x
  const dy = bbox.topRight.y - bbox.topLeft.y

  return Math.atan2(dy, dx)
}

/**
 * Migration helper - now properly handles coordinate systems
 */
export const migrateBoundingBoxToPoints = (
  box: AnnotationDataBoundingBox,
  imageWidth?: number,
  imageHeight?: number
): AnnotationDataBoundingBox => {
  // If points exist and are valid, ensure min/max is derived from them
  if (box.points && box.points.length === 8) {
    // Detect whether points look normalized (0–1) or already in pixel space
    const maxX = Math.max(box.points[0], box.points[2], box.points[4], box.points[6])
    const maxY = Math.max(box.points[1], box.points[3], box.points[5], box.points[7])
    const looksNormalized = maxX <= 1 && maxY <= 1 && (maxX > 0 || maxY > 0)

    let pixelPoints = box.points
    // Only convert from normalized → pixels when they actually look normalized
    if (looksNormalized && imageWidth && imageHeight) {
      pixelPoints = normalizedPointsToPixels(box.points, imageWidth, imageHeight)
    }

    const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(pixelPoints)
    return {
      ...box,
      xmin,
      ymin,
      xmax,
      ymax,
    }
  }

  // If only min/max exist, create normalized points
  if (
    box.xmin !== undefined &&
    box.ymin !== undefined &&
    box.xmax !== undefined &&
    box.ymax !== undefined
  ) {
    return {
      ...box,
      points: createAxisAlignedPoints(
        box.xmin,
        box.ymin,
        box.xmax,
        box.ymax,
        imageWidth,
        imageHeight
      ),
    }
  }

  // Return box as-is if neither condition is met
  return box
}

/**
 * Create a new oriented bounding box from two points - handles coordinate conversion
 */
export const createBoundingBoxFromPoints = (
  startPoint: Point,
  endPoint: Point,
  imageWidth?: number,
  imageHeight?: number
): AnnotationDataBoundingBox => {
  const xmin = Math.min(startPoint.x, endPoint.x)
  const ymin = Math.min(startPoint.y, endPoint.y)
  const xmax = Math.max(startPoint.x, endPoint.x)
  const ymax = Math.max(startPoint.y, endPoint.y)

  return {
    name: 'newBox',
    points: createAxisAlignedPoints(xmin, ymin, xmax, ymax, imageWidth, imageHeight),
    ai_labeled: false,
    confidence: undefined,
    class_index: 0,
    xmin,
    ymin,
    xmax,
    ymax,
  }
}
